import { useEffect, useState } from "react";
import grok from "../images/llms/grok.svg";
import openai from "../images/llms/openai.svg";
import anthropic from "../images/llms/anthropic.svg";
import claude from "../images/llms/claude-color.svg";
import gemini from "../images/llms/gemini-color.svg";
import deepseek from "../images/llms/deepseek-color.svg";
import perplexity from "../images/llms/perplexity-color.svg";

import { Image, Text, useComputedColorScheme } from "@mantine/core";
import { useLlmProvidersStore } from "./llmProvidersStore";
import { GOOGLE_SURFACES, resolveSurface } from "./googleSurfaces";

export default function LlmProviderLogo({
  llmProvider,
  size = 20,
  showName = false,
}) {
  const { getLlmProviderById } = useLlmProvidersStore();
  const [providerName, setProviderName] = useState(null);

  const invertForDarkMode = ["anthropic", "grok", "openai"];

  const darkMode =
    useComputedColorScheme() === "dark" &&
    providerName &&
    invertForDarkMode.includes(providerName.toLowerCase());

  useEffect(() => {
    // console.log("Resolving LLM Provider:", llmProvider);

    let isMounted = true;
    let invert = darkMode; // Invert colors for dark mode
    async function resolveProvider() {
      let name = null;
      const surfaceKey = Array.isArray(llmProvider) ? llmProvider[0] : llmProvider;
      if (!llmProvider) {
        name = "openai";
      } else if (GOOGLE_SURFACES[surfaceKey]) {
        name = surfaceKey;
      } else if (typeof llmProvider === "string") {
        // Try to fetch by id, fallback to name
        const provider = await getLlmProviderById(llmProvider);
        if (provider && provider.name) {
          name = provider.name;
        } else {
          name = llmProvider;
        }
      } else if (Array.isArray(llmProvider)) {
        // Use the first id in the array
        const firstId = llmProvider[0];
        const provider = await getLlmProviderById(firstId);
        // console.log("Provider from array:", provider);
        name = provider?.name || "Unknown";
      } else if (typeof llmProvider === "object" && llmProvider.name) {
        name = llmProvider.name;
      } else {
        name = "OpenAI";
      }
      // console.log("Resolved LLM Provider Name:", name);
      if (isMounted) setProviderName(name);
    }
    resolveProvider();
    return () => {
      isMounted = false;
    };
  }, [llmProvider, getLlmProviderById]);

  const llmProviderMap = {
    grok: grok,
    openai: openai,
    anthropic: anthropic,
    perplexity: perplexity,
    claude: claude,
    gemini: gemini,
    deepseek: deepseek,
  };

  // console.log("Rendering LLM Provider:", llmProvider, providerName);

  const surface = resolveSurface(providerName);
  if (surface) {
    const { Icon, label } = surface;
    return (
      <>
        <Icon size={size} stroke={1.5} />
        {showName && (
          <Text fz="sm" c="dimmed">
            {label}
          </Text>
        )}
      </>
    );
  }

  return (
    <>
      <Image
        w={size}
        h={size}
        src={llmProviderMap[providerName?.toLowerCase() ?? "Unknown"]}
        alt={providerName}
        style={{ filter: darkMode ? "invert(1)" : "none" }}
      />
      {showName && (
        <Text fz="sm" c="dimmed">
          {providerName}
        </Text>
      )}
    </>
  );
}
